import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Save } from 'lucide-react';
import AvatarUpload from './AvatarUpload';

interface UserProfile {
  id: string;
  user_id: string;
  username: string;
  display_name: string;
  bio?: string;
  avatar_url?: string;
  is_public: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProfileFormValues {
  fullName: string;
  bio: string;
  username: string;
}

interface UserProfileFormProps {
  user: UserProfile;
  onSave: (values: ProfileFormValues) => void;
  onCancel: () => void;
}

const UserProfileForm: React.FC<UserProfileFormProps> = ({
  user,
  onSave,
  onCancel
}) => {
  const form = useForm<ProfileFormValues>({
    defaultValues: {
      fullName: user?.display_name || user?.full_name || '',
      bio: user?.bio || '',
      username: user?.username || '',
    }
  });

  // Update form when user data changes
  useEffect(() => {
    if (user) {
      form.reset({
        fullName: user?.display_name || user?.full_name || '',
        bio: user?.bio || '',
        username: user?.username || '',
      });
    }
  }, [user, form]);

  const handleAvatarUpdated = (url: string) => {
    // The avatar update is handled in the AvatarUpload component
    // We don't need to do anything here as the user metadata is updated automatically
  };

  return (
    <div className="bg-background rounded-lg shadow-xs border p-6">
      <h2 className="text-2xl font-bold text-foreground mb-6">Edit Profile</h2>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSave)} className="space-y-6">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="shrink-0">
              <AvatarUpload 
                currentAvatarUrl={user?.avatar_url}
                userId={user?.user_id}
                onAvatarUpdated={handleAvatarUpdated}
              />
            </div>
            
            <div className="flex-1 space-y-4">
              <FormField
                control={form.control}
                name="fullName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Display Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Your display name" {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Username</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="your-username" 
                        {...field}
                        onChange={(e) => {
                          // Auto-format username (lowercase, no spaces)
                          const value = e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '');
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="bio"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Bio</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Tell us about yourself and your display" 
                        className="resize-none" 
                        rows={4}
                        {...field} 
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex items-center gap-2">
                  <Save size={16} />
                  Save Changes
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={onCancel}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default UserProfileForm;
